import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, Heart, Users, Award } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const AboutSection = () => {
  const { author } = useSiteData();
  const authorData = author || {};
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const values = [
    {
      icon: Target,
      title: 'लक्ष्य',
      titleHindi: 'हमारा उद्देश्य',
      description: authorData.mission,
    },
    {
      icon: Heart,
      title: 'सेवा',
      titleHindi: 'समाज सेवा',
      description: 'शिक्षा, संस्कृति और सामाजिक कार्य के माध्यम से समाज की सेवा हेतु समर्पित।',
    },
    {
      icon: Users,
      title: 'समाज',
      titleHindi: 'संगठन',
      description: 'भारतीय मूल्यों और परंपराओं पर आधारित सशक्त, संगठित समाज का निर्माण।',
    },
    {
      icon: Award,
      title: 'उत्कृष्टता',
      titleHindi: 'राष्ट्र निर्माण',
      description: 'साहित्य, समाज सेवा और राष्ट्र निर्माण में उत्कृष्टता हेतु प्रतिबद्ध।',
    },
  ];

  return (
    <section id="about" ref={ref} className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50" />

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            लेखक परिचय
          </h2>
          <p 
            className="text-xl text-orange-600 font-semibold"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            राष्ट्रचिंतक • चिंतक लेखक
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Author Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={authorData.image}
                alt={authorData.name}
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
            </div>
            
            {/* Floating Badge */}
            <motion.div
              className="absolute -bottom-6 -right-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-2xl shadow-2xl"
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="text-center">
                <div className="text-3xl font-bold">50K+</div>
                <div className="text-sm" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>पाठक</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Author Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
              {authorData.name}
            </h3>
            <p className="text-lg text-orange-600 font-semibold" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
              {authorData.title}
            </p>
            <p className="text-gray-700 leading-relaxed text-lg" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
              {authorData.bio}
            </p>
            
            {/* Key Highlights */}
            <div className="space-y-3">
              {(authorData.achievements || []).map((ach, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-700" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{ach}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-4"
              >
                <value.icon className="h-7 w-7 text-white" />
              </motion.div>
              
              <h4 className="text-lg font-bold text-blue-900 mb-1" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
                {value.title}
              </h4>
              <p 
                className="text-sm text-orange-600 font-semibold mb-3"
                style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
              >
                {value.titleHindi}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;